// Mantine
import { Modal, TextInput, NumberInput, Select, Button } from '@mantine/core'

// UI
import ComponentCategorySelect from '../ui/ComponentCategorySelect'

// React
import { useState, useEffect } from 'react'


// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

import ProductCollectionsSelect from '../products/ProductCollectionsSelect'

const WATCH_GUIDE_URL_PATTERN = /^\/guides\/[a-z0-9-]+$/

export default function ComponentEdit({
    component,
    onSaved,
    editComponentOpened,
    editComponentHandlers,
}) {
    // Component category
    const [category, setCategory] = useState(component.category)

    // Component name
    const [name, setName] = useState(component.name)

    // Component value
    const [value, setValue] = useState(component.value)
    const [valueCurrency, setValueCurrency] = useState(
        component.value_currency ?? 'EUR'
    )

    // Component inventory cycle
    const [inventoryCycleDays, setInventoryCycleDays] = useState(
        component.inventory_cycle_days
    )

    // Warranty visible by all retailers
    const [warrantyVisibleByAllRetailers, setWarrantyVisibleByAllRetailers] =
        useState(component.warranty_visible_by_all_retailers)

    // Warranty visible on website contact
    const [
        warrantyVisibleOnWebsiteContact,
        setWarrantyVisibleOnWebsiteContact,
    ] = useState(component.warranty_visible_on_website_contact)

    // Watch guide shop path, e.g. /guides/aquascaphe-classic
    const [watchGuideUrl, setWatchGuideUrl] = useState(
        component.watch_guide_url ?? ''
    )
    const isWatchGuideUrlValid =
        watchGuideUrl === '' || WATCH_GUIDE_URL_PATTERN.test(watchGuideUrl)

    useEffect(() => {
        if (editComponentOpened) {
            reset()
        }
    }, [editComponentOpened])

    // Collections
    const [collections, setCollections] = useState(
        component.collections.map((c) => c.id.toString())
    )

    // Component save
    const [saving, setSaving] = useState(false)

    const { requestPost } = useAxiosApi()

    const onComponentSave = async () => {
        setSaving(true)

        const { data, error } = await requestPost(
            `/components/${component.id}/edit`,
            {
                id: component.id,
                category,
                name,
                value,
                value_currency: valueCurrency,
                inventory_cycle_days:
                    inventoryCycleDays === 0 ? null : inventoryCycleDays,
                warranty_visible_by_all_retailers:
                    warrantyVisibleByAllRetailers,
                warranty_visible_on_website_contact:
                    warrantyVisibleOnWebsiteContact,
                watch_guide_url: watchGuideUrl === '' ? null : watchGuideUrl,
                collections: collections.map((c) => parseInt(c)),
            }
        )

        await onSaved()

        setSaving(false)
        editComponentHandlers.close()
    }

    // Reset on component edit open
    const reset = () => {
        setCategory(component.category)
        setName(component.name)
        setValue(component.value)
        setValueCurrency(component.value_currency ?? 'EUR')
        setInventoryCycleDays(component.inventory_cycle_days)
        setSaving(false)
        setWarrantyVisibleByAllRetailers(
            component.warranty_visible_by_all_retailers
        )
        setWarrantyVisibleOnWebsiteContact(
            component.warranty_visible_on_website_contact
        )
        setWatchGuideUrl(component.watch_guide_url ?? '')
        setCollections(
            component.collections.map((c) => c.id.toString())
        )
    }

    return (
        <Modal
            opened={editComponentOpened}
            onClose={editComponentHandlers.close}
            title="Modifier le composant"
        >
            <div className="flex flex-col gap-4">
                <ComponentCategorySelect
                    value={category}
                    onChange={setCategory}
                    allowDeselect={false}
                />

                <TextInput
                    label="Nom du composant"
                    value={name}
                    onChange={(e) => setName(e.currentTarget.value)}
                />

                <div className="flex flex-col">
                    <span className="text-sm font-medium">
                        Valeur d'inventaire
                    </span>

                    <div className="grid grid-cols-[1fr_120px] gap-2 mt-0.5">
                        <NumberInput
                            clearable
                            allowDecimal={true}
                            decimalScale={2}
                            fixedDecimalScale
                            min={0}
                            clampBehavior="strict"
                            value={value}
                            onChange={setValue}
                            decimalSeparator=","
                        />

                        <Select
                            data={['EUR', 'USD']}
                            value={valueCurrency}
                            onChange={setValueCurrency}
                            allowDeselect={false}
                        />
                    </div>
                </div>

                <div className="flex flex-col">
                    <span className="text-sm font-medium">
                        Cycle d'inventaire
                    </span>

                    <NumberInput
                        clearable
                        allowDecimal={false}
                        min={0}
                        clampBehavior="strict"
                        value={inventoryCycleDays}
                        suffix=" jours"
                        onChange={setInventoryCycleDays}
                        description="Mettre à 0 pour désactiver le cycle d'inventaire"
                    />
                </div>

                <Select
                    label="Garantie visible par tous les revendeurs"
                    value={warrantyVisibleByAllRetailers ? 'Oui' : 'Non'}
                    onChange={(value) =>
                        setWarrantyVisibleByAllRetailers(value === 'Oui')
                    }
                    data={['Oui', 'Non']}
                    allowDeselect={false}
                />

                <Select
                    label="Visible sur le formulaire de contact du site"
                    value={warrantyVisibleOnWebsiteContact ? 'Oui' : 'Non'}
                    onChange={(value) =>
                        setWarrantyVisibleOnWebsiteContact(value === 'Oui')
                    }
                    data={['Oui', 'Non']}
                    allowDeselect={false}
                />

                {category === 'watch_head' && (
                    <TextInput
                        label="Guide d'utilisation"
                        placeholder="/guides/aquascaphe-classic"
                        description="Chemin du guide sur le site, qui doit exister et être publié dans Storyblok (next/guides/…). Vide : pas de bouton guide sur la page garantie."
                        value={watchGuideUrl}
                        onChange={(e) =>
                            setWatchGuideUrl(e.currentTarget.value.trim())
                        }
                        error={
                            !isWatchGuideUrlValid &&
                            'Format attendu : /guides/slug-du-guide'
                        }
                    />
                )}

                <ProductCollectionsSelect
                    label="Collections"
                    value={collections}
                    onChange={setCollections}
                    searchable={true}
                />

                <Button
                    onClick={onComponentSave}
                    disabled={name.length === 0 || !isWatchGuideUrlValid}
                    loading={saving}
                >
                    Enregistrer les modifications
                </Button>
            </div>
        </Modal>
    )
}
